import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBell, FaCheck, FaTrashAlt, FaSearch, FaFilter,
  FaComments, FaFileInvoice, FaCalendarAlt, FaClipboardCheck, FaCreditCard, FaFolderOpen
} from 'react-icons/fa';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../context/ToastContext';

const timeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

const getNotifStyle = (type) => {
  switch (type) {
    case 'chat':
      return { icon: FaComments, bg: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400', badge: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'invoice':
      return { icon: FaFileInvoice, bg: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'meeting':
      return { icon: FaCalendarAlt, bg: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400', badge: 'bg-purple-50 text-purple-700 border-purple-200' };
    case 'checklist':
      return { icon: FaClipboardCheck, bg: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
    case 'payment':
    case 'service_purchase':
      return { icon: FaCreditCard, bg: 'bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400', badge: 'bg-pink-50 text-pink-700 border-pink-200' };
    case 'document':
      return { icon: FaFolderOpen, bg: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400', badge: 'bg-cyan-50 text-cyan-700 border-cyan-200' };
    default:
      return { icon: FaBell, bg: 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400', badge: 'bg-gray-50 text-gray-700 border-gray-200' };
  }
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const toast = useToast();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/notifications/admin`);
      if (!response.ok) throw new Error('Failed to fetch notifications');
      const data = await response.json();
      setNotifications(data || []);
    } catch (err) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, toast]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Group chat notifications by sender
  const groupedNotifications = useMemo(() => {
    const chatGroups = new Map();
    const result = [];

    for (const notif of notifications) {
      const isChat = notif.type === 'chat' || (notif.title && notif.title.toLowerCase().includes('new message from'));
      if (isChat) {
        const senderKey = notif.metadata?.chatUserId || notif.metadata?.senderName || notif.title;
        if (chatGroups.has(senderKey)) {
          const existing = chatGroups.get(senderKey);
          existing.count += 1;
          existing.ids.push(notif._id);
          if (!notif.isRead) existing.hasUnread = true;
        } else {
          const groupObj = {
            ...notif,
            count: 1,
            ids: [notif._id],
            hasUnread: !notif.isRead,
          };
          chatGroups.set(senderKey, groupObj);
          result.push(groupObj);
        }
      } else {
        result.push({
          ...notif,
          count: 1,
          ids: [notif._id],
          hasUnread: !notif.isRead,
        });
      }
    }

    return result;
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    return groupedNotifications.filter((notif) => {
      const matchSearch =
        !searchQuery ||
        notif.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notif.message?.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchSearch) return false;

      if (activeTab === 'unread') return notif.hasUnread;
      if (activeTab === 'chats') return notif.type === 'chat';
      if (activeTab === 'documents') return notif.type === 'document';
      if (activeTab === 'other') return notif.type !== 'chat' && notif.type !== 'document';

      return true;
    });
  }, [groupedNotifications, searchQuery, activeTab]);

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_BASE}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ forAdmin: true }),
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleMarkSingleRead = async (e, notif) => {
    e.stopPropagation();
    const token = localStorage.getItem('adminToken');
    const targetIds = notif.ids || [notif._id];

    setNotifications((prev) =>
      prev.map((n) => (targetIds.includes(n._id) ? { ...n, isRead: true } : n))
    );

    targetIds.forEach((id) => {
      fetch(`${API_BASE}/api/notifications/mark-read/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).catch(console.error);
    });

    toast.info('Notification marked as read');
  };

  const handleDeleteNotification = async (e, notif) => {
    e.stopPropagation();
    if (!window.confirm('Delete this notification?')) return;

    const targetIds = notif.ids || [notif._id];
    setNotifications((prev) => prev.filter((n) => !targetIds.includes(n._id)));

    targetIds.forEach((id) => {
      fetch(`${API_BASE}/api/notifications/${id}`, {
        method: 'DELETE',
      }).catch(console.error);
    });

    toast.success('Notification deleted');
  };

  const handleItemClick = (notif) => {
    if (notif.hasUnread) {
      handleMarkSingleRead({ stopPropagation: () => {} }, notif);
    }
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const unreadTotal = notifications.filter((n) => !n.isRead).length;

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="Notifications Hub"
        description="Manage all system alerts, user messages, document uploads, and activities"
        icon={FaBell}
        badge={
          unreadTotal > 0 && (
            <span className="px-3 py-1 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold uppercase">
              {unreadTotal} Unread
            </span>
          )
        }
        actions={
          unreadTotal > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs font-bold rounded-xl shadow-md hover:shadow-lg transition-all cursor-pointer uppercase"
            >
              <FaCheck className="w-3.5 h-3.5" /> Mark All Read
            </button>
          )
        }
      />

      {/* Filter and Search Bar */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl p-4 shadow-card border border-gray-100 dark:border-dark-700 flex flex-col md:flex-row gap-4 items-center justify-between">
        {/* Tabs */}
        <div className="flex border-b md:border-b-0 border-gray-100 dark:border-dark-700 overflow-x-auto w-full md:w-auto gap-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'unread', label: 'Unread' },
            { id: 'chats', label: 'Chats' },
            { id: 'documents', label: 'Documents' },
            { id: 'other', label: 'Other Alerts' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-xs font-bold rounded-xl whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-primary-600 text-white shadow-md shadow-primary-500/20'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full md:w-72">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search notifications..."
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
          />
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden divide-y divide-gray-100 dark:divide-dark-700">
        {filteredNotifications.length === 0 ? (
          <EmptyState
            preset="search"
            title="No Notifications Found"
            description="No notifications match your current filter or search criteria."
          />
        ) : (
          filteredNotifications.map((notif) => {
            const style = getNotifStyle(notif.type);
            const IconComp = style.icon;

            return (
              <div
                key={notif._id}
                onClick={() => handleItemClick(notif)}
                className={`p-4 transition-colors cursor-pointer flex items-center justify-between gap-4 ${
                  notif.hasUnread
                    ? 'bg-primary-50/30 dark:bg-primary-900/10'
                    : 'hover:bg-gray-50/80 dark:hover:bg-dark-700/50'
                }`}
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${style.bg}`}>
                    <IconComp className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="font-bold text-sm text-gray-900 dark:text-white truncate">{notif.title}</h4>
                      {notif.count > 1 && (
                        <span className="px-2 py-0.5 text-[10px] font-black bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">
                          {notif.count > 9 ? '9+' : `${notif.count}+`}
                        </span>
                      )}
                      {notif.hasUnread && (
                        <span className="px-2 py-0.5 text-[9px] font-black uppercase bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-md">
                          Unread
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{notif.message}</p>
                    <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mt-1 block">
                      {timeAgo(notif.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {notif.hasUnread && (
                    <button
                      type="button"
                      onClick={(e) => handleMarkSingleRead(e, notif)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl transition-colors cursor-pointer"
                      title="Mark as read"
                    >
                      <FaCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleDeleteNotification(e, notif)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors cursor-pointer"
                    title="Delete notification"
                  >
                    <FaTrashAlt className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </PageLayout>
  );
};

export default Notifications;
